import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { siblingsFor, parseVariant } from "@/lib/products";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, ArrowLeft, Warning, FilePdf, Bell } from "@phosphor-icons/react";
import { VialImage } from "@/components/VialImage";

const Spec = ({ label, value }) => value ? (
  <div className="flex justify-between py-3 border-b border-black/10">
    <span className="label-mono text-muted-foreground">{label}</span>
    <span className="font-mono text-sm text-foreground">{value}</span>
  </div>
) : null;

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notified, setNotified] = useState(false);

  useEffect(() => {
    setQty(1);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {});
  }, []);

  if (notFound) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-muted-foreground font-mono">Compound not found. <Link to="/shop" className="text-primary">Back to catalog</Link></div>;
  if (!product) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-muted-foreground font-mono">Loading…</div>;

  const siblings = siblingsFor(product, allProducts);
  const hasSizes = siblings.length > 1;
  const baseName = hasSizes ? parseVariant(product.name).base : product.name;
  const currentSize = parseVariant(product.name).size;

  const subscribeRestock = async () => {
    if (!notifyEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail)) {
      toast.error("Please enter a valid email");
      return;
    }
    try {
      await api.post("/restock/subscribe", { product_id: product.id, email: notifyEmail });
      setNotified(true);
      toast.success("We'll email you when it's back in stock");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not subscribe");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 label-mono text-muted-foreground hover:text-primary transition-colors mb-8">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-12">
        <div className="relative aspect-square border border-black/10 overflow-hidden bg-white">
          {product.image_url ? (
            <>
              <img src={product.image_url} alt={product.name} className="h-full w-full object-contain" />
              {product.bundle_items?.length > 0 && (
                <div className="absolute left-0 right-0 flex justify-around px-[9%] pointer-events-none" style={{ top: "50%", transform: "translateY(-50%)" }}>
                  {product.bundle_items.slice(0, 3).map((it, i) => (
                    <span key={i} className="w-[26%] text-center font-bold uppercase leading-[1.1] text-[#6B5512]" style={{ fontFamily: "'Cinzel', serif", fontSize: "13px" }}>{it}</span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <VialImage name={product.name} large />
          )}
          <span className="absolute top-4 left-4 label-mono border border-primary/30 text-primary bg-primary/10 px-3 py-1">
            {product.purity} Purity
          </span>
        </div>

        <div>
          <span className="label-mono text-muted-foreground">{product.category}</span>
          <h1 data-testid="product-title" className="font-head text-4xl mt-2 mb-4 leading-tight">{baseName}</h1>

          {hasSizes && (
            <div data-testid="detail-size-select" className="flex flex-wrap gap-2 mb-6">
              <span className="label-mono text-muted-foreground w-full mb-1">Size / Strength</span>
              {siblings.map((v) => (
                <button
                  key={v.id}
                  data-testid={`detail-size-opt-${v.id}`}
                  onClick={() => { if (v.id !== product.id) nav(`/product/${v.id}`); }}
                  className={`label-mono px-4 py-2 border transition-colors ${v._size === currentSize ? "border-primary text-primary bg-primary/10" : "border-black/15 text-muted-foreground hover:border-black/30"}`}
                  style={{ transition: "color 0.2s, border-color 0.2s, background-color 0.2s" }}
                >
                  {v._size}
                </button>
              ))}
            </div>
          )}

          <p className="text-muted-foreground leading-relaxed mb-8">{product.description}</p>

          <div className="mb-8">
            <Spec label="Molecular Weight" value={product.molecular_weight} />
            <Spec label="Purity" value={product.purity} />
            <Spec label="In Stock" value={product.stock > 0 ? `${product.stock} units` : "Out of stock"} />
          </div>

          <div className="flex items-end justify-between mb-8">
            <span className="font-mono text-4xl">${product.price?.toFixed(2)}</span>
            <div className="flex items-center border border-black/15">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-primary transition-colors"><Minus size={16} /></button>
              <span data-testid="qty-value" className="font-mono w-10 text-center">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(product.stock || 1, q + 1))} className="p-3 hover:text-primary transition-colors"><Plus size={16} /></button>
            </div>
          </div>

          {(product.stock ?? 0) <= 0 ? (
            <div data-testid="detail-out-of-stock" className="space-y-4">
              <div className="flex items-center justify-center gap-2 border border-destructive/30 bg-destructive/5 text-destructive label-mono py-4">
                <Warning size={18} weight="bold" /> Out of Stock — check back soon
              </div>
              {notified ? (
                <div className="flex items-center justify-center gap-2 border border-accent/30 bg-accent/5 text-accent label-mono py-4">
                  <Bell size={18} weight="duotone" /> You're on the list — we'll email you
                </div>
              ) : (
                <div className="border border-primary/20 bg-primary/5 p-4">
                  <span className="label-mono text-primary flex items-center gap-2 mb-3"><Bell size={16} /> Notify me when back in stock</span>
                  <div className="flex gap-2">
                    <input
                      data-testid="restock-email"
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="flex-1 bg-white border border-black/15 px-4 py-3 font-mono text-sm focus:border-primary outline-none transition-colors"
                    />
                    <button
                      data-testid="restock-subscribe"
                      onClick={subscribeRestock}
                      className="bg-primary text-black label-mono px-5 hover:bg-[#E6C766] transition-colors"
                      style={{ transition: "background-color 0.2s" }}
                    >
                      Notify Me
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-4">
              <button
                data-testid="detail-add-to-cart"
                onClick={() => { add(product, qty); toast.success(`${qty} × ${product.name} added to cart`); }}
                className="flex-1 flex items-center justify-center gap-2 bg-primary text-black label-mono py-4 hover:bg-[#E6C766] transition-colors"
                style={{ transition: "background-color 0.2s" }}
              >
                <ShoppingBag size={18} weight="bold" /> Add to Cart
              </button>
              <button
                data-testid="detail-buy-now"
                onClick={() => { add(product, qty); nav("/cart"); }}
                className="flex-1 border border-black/15 label-mono py-4 hover:border-primary hover:text-primary transition-colors"
                style={{ transition: "color 0.2s, border-color 0.2s" }}
              >
                Buy Now
              </button>
            </div>
          )}

          {product.coa_path && (
            <a
              data-testid="download-coa"
              href={`${API}/coa/${product.id}`}
              target="_blank"
              rel="noreferrer"
              className="mt-4 w-full flex items-center justify-center gap-2 border border-primary/40 text-primary label-mono py-3 hover:bg-primary/10 transition-colors"
              style={{ transition: "background-color 0.2s" }}
            >
              <FilePdf size={18} weight="duotone" /> Download Certificate of Analysis
            </a>
          )}

          <div className="mt-8 flex gap-3 border border-primary/20 bg-primary/5 p-4">
            <Warning size={20} className="text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              This product is intended <span className="text-primary">for research use only</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
